// ============================================================================
// CaseClosurePanel — right-pane panel for the "Closure" sidebar item
// ============================================================================
// Three layers:
//   1. Current-state card — either the active closure record or a "Case is
//      open" placeholder with the Close-Case CTA.
//   2. Action button row — Close (when open) or Reopen (when closed), each
//      gated by the existing permissions.
//   3. History list — all past closure rows (including reopened ones), most
//      recent first.

import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  useActiveCaseClosure,
  // history fetcher
} from 'hooks/api/useCaseClosure';
import { caseClosureService } from 'api/services/caseClosureService';
import type { LitigationCase } from 'api/types/litigationCase.types';
import type { CaseClosure, OutcomeCategory } from 'api/types/caseClosure.types';
import type { useRecsuitPermissions } from 'hooks/useRecsuitPermissions';
import CloseCaseModal from '../components/CloseCaseModal';
import ReopenCaseModal from '../components/ReopenCaseModal';

const OUTCOME_CHIP: Record<OutcomeCategory, { label: string; bg: string; fg: string }> = {
  FAVOURABLE:   { label: 'Win',     bg: '#dcfce7', fg: '#166534' },
  UNFAVOURABLE: { label: 'Lose',    bg: '#fee2e2', fg: '#991b1b' },
  NEUTRAL:      { label: 'Neutral', bg: 'var(--bg-secondary)', fg: 'var(--text-secondary)' },
};

export interface CaseClosurePanelProps {
  kase: LitigationCase;
  permissions: ReturnType<typeof useRecsuitPermissions>;
  onMutated: () => void;
}

const CaseClosurePanel: React.FC<CaseClosurePanelProps> = ({ kase, permissions, onMutated }) => {
  const { data: active, loading, refetch } = useActiveCaseClosure(kase.id);

  const [history, setHistory] = useState<CaseClosure[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = React.useCallback(async () => {
    setHistoryLoading(true);
    try { setHistory(await caseClosureService.getHistory(kase.id)); }
    finally { setHistoryLoading(false); }
  }, [kase.id]);

  React.useEffect(() => { void loadHistory(); }, [loadHistory]);

  const refresh = React.useCallback(async () => {
    await refetch();
    await loadHistory();
    onMutated();
  }, [refetch, loadHistory, onMutated]);

  const isClosed = kase.caseStatus === 'CLOSED';
  const canClose  = !isClosed && permissions.hasAction('litigation-case', 'close')
                    && !!kase.workflowInstanceId;
  const canReopen = isClosed && permissions.hasAction('litigation-case', 'reopen');

  const [closeOpen,  setCloseOpen]  = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);

  return (
    <div style={{ padding: '0.25rem 0' }}>
      <h3 style={headerStyle}>
        <IconSystem.check size={18} style={{ marginRight: '0.45rem', verticalAlign: '-3px' }} />
        Closure
      </h3>

      {/* Current state card */}
      {loading ? (
        <div style={mutedBlock}>Loading…</div>
      ) : !active ? (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Case is open</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            No closure recorded. Use <strong>Close Case</strong> to record an outcome and freeze the workflow.
          </div>
        </div>
      ) : (
        <ActiveClosureCard closure={active} />
      )}

      {/* Action buttons */}
      <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem' }}>
        {canClose && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCloseOpen(true)}
          >
            <IconSystem.check size={14} style={{ marginRight: '0.4rem' }} />
            Close Case
          </button>
        )}
        {canReopen && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setReopenOpen(true)}
          >
            <IconSystem.refresh size={14} style={{ marginRight: '0.4rem' }} />
            Reopen Case
          </button>
        )}
        {!canClose && !canReopen && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            {isClosed
              ? 'You don\'t have permission to reopen this case.'
              : !kase.workflowInstanceId
                ? 'The case must be claimed (workflow started) before closure.'
                : 'You don\'t have permission to close this case.'}
          </span>
        )}
      </div>

      {/* History */}
      <h4 style={{ ...headerStyle, fontSize: '0.85rem', marginTop: '1.5rem' }}>History</h4>
      {historyLoading ? (
        <div style={mutedBlock}>Loading history…</div>
      ) : history.length === 0 ? (
        <div style={mutedBlock}>No closure events yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {history.map((h) => <HistoryRow key={h.id} h={h} />)}
        </div>
      )}

      <CloseCaseModal
        isOpen={closeOpen}
        caseId={kase.id}
        caseCode={kase.caseCode}
        onClose={() => setCloseOpen(false)}
        onClosed={refresh}
      />
      <ReopenCaseModal
        isOpen={reopenOpen}
        caseId={kase.id}
        caseCode={kase.caseCode}
        onClose={() => setReopenOpen(false)}
        onReopened={refresh}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------

const ActiveClosureCard: React.FC<{ closure: CaseClosure }> = ({ closure }) => {
  const chip = OUTCOME_CHIP[closure.outcomeCategory];
  return (
    <div style={{
      padding: '0.85rem 1rem',
      border: '1px solid var(--border-color)',
      borderLeft: `4px solid ${chip.fg}`,
      borderRadius: 6,
      background: 'var(--bg-primary)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <span style={{
          padding: '2px 10px', borderRadius: '999px',
          fontSize: '0.72rem', fontWeight: 600,
          background: chip.bg, color: chip.fg as string,
        }}>{chip.label}</span>
        <strong style={{ fontSize: '0.9rem' }}>{closure.closureName ?? closure.closureCode}</strong>
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginBottom: '0.35rem' }}>
        Closed by <strong style={{ color: 'var(--text-primary)' }}>{closure.closedByName}</strong>
        {' · '}{new Date(closure.closedAt).toLocaleString('en-IN')}
        {closure.sourceStateCode ? ` · from state ${closure.sourceStateCode}` : ''}
      </div>
      {closure.recoveredAmount != null && closure.recoveredAmount > 0 && (
        <div style={{ fontSize: '0.82rem', marginBottom: '0.35rem' }}>
          Recovered: <strong>₹{closure.recoveredAmount.toLocaleString('en-IN')}</strong>
        </div>
      )}
      <div style={{
        fontSize: '0.82rem', fontStyle: 'italic',
        padding: '0.5rem 0.75rem',
        background: 'var(--bg-secondary)',
        borderRadius: 4,
        marginTop: '0.35rem',
      }}>"{closure.closureRemarks}"</div>
    </div>
  );
};

const HistoryRow: React.FC<{ h: CaseClosure }> = ({ h }) => {
  const chip = OUTCOME_CHIP[h.outcomeCategory];
  const reopened = !!h.reopenedAt;
  return (
    <div style={{
      padding: '0.6rem 0.85rem',
      border: '1px solid var(--border-color)',
      borderRadius: 4,
      background: reopened ? 'var(--bg-secondary)' : 'var(--bg-primary)',
      fontSize: '0.8rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
        <span style={{
          padding: '1px 8px', borderRadius: '999px',
          fontSize: '0.65rem', fontWeight: 600,
          background: chip.bg, color: chip.fg as string,
        }}>{chip.label}</span>
        <strong>{h.closureName ?? h.closureCode}</strong>
        {reopened && (
          <span style={{
            marginLeft: 'auto', fontSize: '0.65rem', fontWeight: 600,
            padding: '1px 6px', borderRadius: '999px',
            background: '#fef3c7', color: '#78350f',
          }}>Reopened</span>
        )}
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
        Closed {new Date(h.closedAt).toLocaleString('en-IN')} by {h.closedByName}
        {reopened && (
          <>
            {' · Reopened '}{new Date(h.reopenedAt!).toLocaleString('en-IN')}
            {h.reopenedByName ? ` by ${h.reopenedByName}` : ''}
          </>
        )}
      </div>
      {h.closureRemarks && (
        <div style={{ marginTop: '0.3rem', fontStyle: 'italic', color: 'var(--text-primary)' }}>
          "{h.closureRemarks}"
        </div>
      )}
      {reopened && h.reopenedReason && (
        <div style={{ marginTop: '0.2rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          Reopen reason: <em>{h.reopenedReason}</em>
        </div>
      )}
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  fontSize: '1rem', fontWeight: 600, margin: '0 0 0.6rem',
  paddingBottom: '0.3rem', borderBottom: '1px solid var(--border-color)',
};

const emptyCardStyle: React.CSSProperties = {
  padding: '0.85rem 1rem',
  border: '1px dashed var(--form-border-color)',
  borderRadius: 6,
  background: 'var(--bg-secondary)',
};

const mutedBlock: React.CSSProperties = {
  padding: '0.65rem 0.85rem',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

export default CaseClosurePanel;
